import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Inventory, InventoryAlert, InventoryLog, InventoryAdjust, Product, ProductCreate, ProductUpdate, InventoryFilters } from "@cold-crew/shared";
import type { PaginatedResponse } from "@cold-crew/shared";

// ─── Query Keys ───────────────────────────────────────────────────────────────

export const inventoryKeys = {
  all:       () => ["inventario"]                          as const,
  stock:     (f?: object) => [...inventoryKeys.all(), "stock", f]    as const,
  alertas:   () => [...inventoryKeys.all(), "alertas"]     as const,
  movs:      (f?: object) => [...inventoryKeys.all(), "movs", f]     as const,
  productos: (f?: object) => [...inventoryKeys.all(), "productos", f] as const,
  producto:  (id: number) => [...inventoryKeys.all(), "producto", id] as const,
};

// ─── Stock ────────────────────────────────────────────────────────────────────

export function useInventario(filters: Partial<InventoryFilters> = {}) {
  return useQuery({
    queryKey: inventoryKeys.stock(filters),
    queryFn:  () => api.get<{ data: (Inventory & { alerta: string; porcentaje_minimo: number })[]; total: number }>("/inventario", filters as any),
  });
}

export function useAlertas() {
  return useQuery({
    queryKey: inventoryKeys.alertas(),
    queryFn:  () => api.get<{ data: InventoryAlert[]; total: number }>("/inventario/alertas"),
    refetchInterval: 60_000, // poll every 60s
  });
}

export function useMovimientos(page = 1, productoId?: number) {
  return useQuery({
    queryKey: inventoryKeys.movs({ page, productoId }),
    queryFn:  () => api.get<PaginatedResponse<InventoryLog & { producto_nombre: string }>>("/inventario/movimientos", { page, producto_id: productoId }),
  });
}

// ─── Ajuste manual ────────────────────────────────────────────────────────────

export function useAjusteManual() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: InventoryAdjust) => api.post<null>("/inventario/ajuste", data),
    onSuccess:  () => qc.invalidateQueries({ queryKey: inventoryKeys.all() }),
  });
}

// ─── Productos ────────────────────────────────────────────────────────────────

export function useProductos(filters: Partial<InventoryFilters> = {}) {
  return useQuery({
    queryKey: inventoryKeys.productos(filters),
    queryFn:  () => api.get<PaginatedResponse<Product>>("/inventario/productos", filters as any),
  });
}

export function useProducto(id: number) {
  return useQuery({
    queryKey: inventoryKeys.producto(id),
    queryFn:  () => api.get<Product>(`/inventario/productos/${id}`),
    enabled:  !!id,
  });
}

export function useCreateProducto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ProductCreate) => api.post<Product>("/inventario/productos", data),
    onSuccess:  () => qc.invalidateQueries({ queryKey: inventoryKeys.all() }),
  });
}

export function useUpdateProducto(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ProductUpdate) => api.put<Product>(`/inventario/productos/${id}`, data),
    onSuccess:  () => qc.invalidateQueries({ queryKey: inventoryKeys.all() }),
  });
}

export function useDeleteProducto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/inventario/productos/${id}`),
    onSuccess:  () => qc.invalidateQueries({ queryKey: inventoryKeys.all() }),
  });
}
